<template>
    <div>
        <v-row class="ma-0 pb-offset">
            <v-col cols="12" class="pa-0">
            <!-- Filtri per le statistiche -->
            <v-card class="mb-4">
                <v-card-title>
                    <v-icon left>mdi-filter</v-icon>
                    Filtri Analisi
                </v-card-title>
                <v-card-text>
                    <fields-generator
                        v-if="fieldsset.fields"
                        class="pt-4"
                        :fieldsset.sync="fieldsset"
                        :model="model"
                        ref="fieldsgenerator"
                    ></fields-generator>
                </v-card-text>
            </v-card>

            <!-- Contenitore per i dati con loader -->
            <div class="position-relative">
                <!-- Loader durante il caricamento dei dati -->
                <v-overlay :value="loading" absolute z-index="5">
                    <v-progress-circular
                        indeterminate
                        size="64"
                        color="primary"
                    ></v-progress-circular>
                </v-overlay>

            <!-- Riepilogo generale -->
            <v-card class="mb-4">
                <v-card-title>
                    <v-icon left color="primary">mdi-chart-box-outline</v-icon>
                    Riepilogo Abbonamenti
                </v-card-title>
                <v-card-text>
                    <!-- Prima riga: Metriche generali -->
                    <v-row>
                        <v-col cols="12" md="3">
                            <v-card outlined class="text-center pa-4">
                                <div class="display-1 secondary--text">{{ summary.totalSubscriptions }}</div>
                                <div class="subtitle-1">Abbonamenti Totali</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-card outlined class="text-center pa-4">
                                <div class="display-1 accent--text">{{ summary.activeSubscriptions }}</div>
                                <div class="subtitle-1">Abbonamenti Attivi</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-card outlined class="text-center pa-4" color="light-blue lighten-5">
                                <div class="display-1 blue--text">{{ summary.monthlyCashFlow | currency }}</div>
                                <div class="subtitle-1">Cash Flow Mensile</div>
                                <div class="caption">(con IVA)</div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-card outlined class="text-center pa-4" color="light-blue lighten-5">
                                <div class="display-1 blue--text">{{ summary.yearlyCashFlowProjection | currency }}</div>
                                <div class="subtitle-1">Proiezione Annuale</div>
                                <div class="caption">(con IVA)</div>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>            <!-- Cash Flow Analysis -->
            <v-row class="mt-4">
                <!-- Messaggio se non ci sono dati -->
                <v-col cols="12" v-if="!hasAnyData">
                    <v-card>
                        <v-card-text class="text-center py-8">
                            <v-icon size="64" color="grey">mdi-chart-line-variant</v-icon>
                            <div class="subtitle-1 mt-4 grey--text">
                                Nessun dato disponibile per i filtri selezionati
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <!-- Tabella Cash Flow dettagliata per gruppo -->
                <v-col cols="12" v-if="cashFlowDetailedData.length > 0">
                    <v-card>
                        <v-card-title class="blue white--text">
                            <v-icon left color="white">mdi-table</v-icon>
                            Cash Flow per {{ groupByLabel }}
                        </v-card-title>
                        <v-card-text>
                            <v-data-table
                                :headers="cashFlowTableHeaders"
                                :items="cashFlowDetailedData"
                                :items-per-page="10"
                                class="elevation-1"
                                :footer-props="{
                                    itemsPerPageText: 'Righe per pagina:',
                                    itemsPerPageAllText: 'Tutto'
                                }"
                            >
                                <template slot="item.cashFlow" slot-scope="{ item }">
                                    <span class="font-weight-bold blue--text">{{ item.cashFlow | currency }}</span>
                                </template>
                                <template slot="item.monthlyRecurring" slot-scope="{ item }">
                                    <span class="blue--text">{{ item.monthlyRecurring | currency }}</span>
                                </template>
                                <template slot="item.quantity" slot-scope="{ item }">
                                    <v-chip small color="grey lighten-2">{{ item.quantity }}</v-chip>
                                </template>
                                <template slot="item.subscriptions" slot-scope="{ item }">
                                    <v-chip small color="primary">{{ item.subscriptions }}</v-chip>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Previsione Cash Flow mensile dettagliata -->
            <v-card class="mt-4" v-if="cashFlowProjections.length > 0">
                <v-card-title>
                    <v-icon left>mdi-calendar-month</v-icon>
                    Previsione Cash Flow Mensile
                </v-card-title>
                <v-card-text>
                    <v-data-table
                        :headers="cashFlowHeaders"
                        :items="cashFlowProjections"
                        :items-per-page.sync="cashFlowItemsPerPage"
                        :page.sync="cashFlowPage"
                        class="elevation-1"
                        :footer-props="{
                            itemsPerPageText: 'Righe per pagina:',
                            itemsPerPageAllText: 'Tutto',
                            itemsPerPageOptions: [6, 12, 24, 36, -1]
                        }"
                    >
                        <template slot="item.expectedAmount" slot-scope="{ item }">
                            <span class="font-weight-bold primary--text">{{ item.expectedAmount | currency }}</span>
                        </template>
                        <template slot="item.dueCount" slot-scope="{ item }">
                            <v-chip small color="primary">{{ item.dueCount }}</v-chip>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
            </div> <!-- Fine contenitore dati con loader -->
            </v-col>
        </v-row>
    </div>
</template>

<script>
import Vue from 'vue'

export default {
    name: 'subscription-statistics',

    data() {
        return {
            fieldsset: {},
            debounce: null,
            model: {
                dateFrom: this.getFirstDayOfYear(),   // inizio anno corrente
                dateTo: this.getLastDayOfYear(), // fine anno corrente
                groupBy: 'product'
            },
            loaded: false,
            loading: false, // Loading locale per i dati

            summary: {
                totalSubscriptions: 0,
                activeSubscriptions: 0,
                monthlyCashFlow: 0,
                yearlyCashFlowProjection: 0
            },

            cashFlowDetailedData: [],
            cashFlowProjections: [],

            cashFlowHeaders: [
                { text: 'Mese', value: 'month', sortable: false },
                { text: 'Importo Previsto', value: 'expectedAmount', sortable: true },
                { text: 'Scadenze', value: 'dueCount', sortable: true }
            ],
            
            // Paginazione per la tabella Cash Flow
            cashFlowItemsPerPage: 12,
            cashFlowPage: 1
        }
    },

    computed: {
        hasAnyData() {
            // Verifica se ci sono dati da mostrare per il cash flow
            const hasCashFlowDetailedData = this.cashFlowDetailedData && this.cashFlowDetailedData.length > 0
            const hasCashFlowProjections = this.cashFlowProjections && this.cashFlowProjections.length > 0
            
            return hasCashFlowDetailedData || hasCashFlowProjections
        },

        groupByLabel() {
            return this.model.groupBy === 'product' ? 'Prodotto' : 
                   this.model.groupBy === 'category' ? 'Categoria' : 'Cliente'
        },

        cashFlowTableHeaders() {
            return [
                { text: 'Nome', value: 'name', sortable: true },
                { text: 'Cash Flow (con IVA)', value: 'cashFlow', sortable: true },
                { text: 'Cash Flow Mensile (con IVA)', value: 'monthlyRecurring', sortable: true },
                { text: 'Quantità', value: 'quantity', sortable: true },
                { text: 'N. Abbonamenti', value: 'subscriptions', sortable: true }
            ]
        }
    },

    created() {
        // Chiudi eventuali progress globali aperti da altre pagine
        this.$store.commit('endProgress')
        
        this.loading = true // Mostra il loading iniziale locale
        this.axios.get('subscriptions-statistics-info').then(response => {
            // get body data
            this.fieldsset = response.data.info.fields

            _.forEach(this.fieldsset.fields, (value, key) => {
                if(value.default) {
                    Vue.set(this.model, value.key, value.default)
                }
            })

            this.loadData()

        }, response => {
            // error callback
            this.loading = false
            this.$store.commit('endProgress') // Assicurati che il progress globale sia chiuso anche in caso di errore
        });
    },

    beforeDestroy() {
        // Pulisci eventuali timeout pendenti e chiudi il progress globale
        if (this.debounce) {
            clearTimeout(this.debounce)
        }
        this.$store.commit('endProgress')
    },

    watch: {
        model: {
            handler() {
                this.loadData()
            },
            deep: true,
        }
    },

    methods: {

        getFirstDayOfYear() {
            const date = new Date()
            date.setMonth(0, 1) // 1 gennaio
            return date.toISOString().substr(0, 10)
        },

        getLastDayOfYear() {
            const date = new Date()
            date.setMonth(11, 31) // 31 dicembre
            return date.toISOString().substr(0, 10)
        },

        async loadData() {
            if(this.debounce) {
                clearTimeout(this.debounce)
            }

            this.debounce = setTimeout(async () => {
                this.loading = true // Usa loading locale invece del progress globale
                
                try {
                    const response = await this.axios.get('/subscriptions-statistics', {
                        params: this.model
                    })
                    
                    // Aggiorna i dati di riepilogo (solo cash flow)
                    this.summary = response.data.summary || {
                        totalSubscriptions: 0,
                        activeSubscriptions: 0,
                        monthlyCashFlow: 0,
                        yearlyCashFlowProjection: 0
                    }
                    
                    // Aggiorna i dati dettagliati
                    this.cashFlowDetailedData = response.data.cashFlowDetailed || []
                    this.cashFlowProjections = response.data.cashFlowProjections || []
                    
                } catch (error) {
                    console.error('Error loading subscription cash flow statistics:', error)
                    
                    // Inizializza i dati con valori vuoti in caso di errore
                    this.summary = {
                        totalSubscriptions: 0,
                        activeSubscriptions: 0,
                        monthlyCashFlow: 0,
                        yearlyCashFlowProjection: 0
                    }
                    this.cashFlowDetailedData = []
                    this.cashFlowProjections = []
                    
                    this.$store.dispatch('showSnackbar', {
                        text: 'Errore nel caricamento delle statistiche cash flow',
                        color: 'error'
                    })
                } finally {
                    this.loading = false // Ferma il loading locale
                }
            }, 500)
        }
    },

    filters: {
        currency(value) {
            if (!value) return '€0,00'
            return new Intl.NumberFormat('it-IT', {
                style: 'currency',
                currency: 'EUR'
            }).format(value)
        }
    }
}
</script>

<style scoped>
.pb-offset {
    padding-bottom: 64px;
}

.position-relative {
    position: relative;
    min-height: 200px; /* Altezza minima per evitare collassi durante il caricamento */
}
</style>
